import { ListItemText } from "@mui/material";
import { useMemo, useState } from "react";
import { useDebounce } from "../hooks/use-debounce";
import {
  UseSearchPatientListProps,
  useSearchPatientList,
} from "../queries/list/useSearchPatientList";
import { NonNullableDate } from "../utils/date-time-utility";
import { DEF_PAGINATION, DGT_Pagination, getXtenantId } from "../utils/utils";
import { AsyncSelectOption } from "./AsyncSelect";
import AsyncSelectScroll from "./AsyncSelectScroll";
import Flex from "./Flex";
import Text from "./Text";

interface PatientSelectScrollProps {
  value: AsyncSelectOption | null;
  onChange: (value: AsyncSelectOption | null) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | boolean;
  patientSearchParam?: UseSearchPatientListProps;
  name?: string;
  handleBlur?: (e: any) => void;
  placeholder?: string;
  excludeUuids?: string[];
  showEmail?: boolean;
}

/**
 * Infinite-scroll variant of {@link PatientSelect}: same patient search, but
 * the dropdown loads more patients on scroll instead of only the first page.
 *
 * @author [Rajkumar Rathod]
 */
const PatientSelectScroll = ({
  value,
  onChange,
  label,
  disabled = false,
  required,
  error,
  patientSearchParam,
  name,
  handleBlur,
  placeholder,
  excludeUuids,
  showEmail = true,
}: PatientSelectScrollProps) => {
  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>(
    patientSearchParam?.pagination ?? DEF_PAGINATION
  );
  const {
    data: patientList,
    isLoading,
    isFetching,
  } = useSearchPatientList({
    xTENANTID: patientSearchParam?.xTENANTID ?? getXtenantId(),
    ...patientSearchParam,
    pagination,
    searchString: searchKey,
  });

  const handleSearch = (v: string) => {
    setSearchKey(v);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const options: AsyncSelectOption[] = useMemo(() => {
    const allOptions =
      patientList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.firstName + " " + it?.lastName,
        birthDate: it?.birthDate,
        email: it?.email ?? "",
      })) ?? [];
    if (excludeUuids?.length) {
      return allOptions.filter((opt) => !excludeUuids.includes(opt.value));
    }
    return allOptions;
  }, [patientList?.data?.content, excludeUuids]);

  return (
    <AsyncSelectScroll
      required={required}
      error={error}
      label={label}
      loading={isFetching || isLoading}
      disabled={disabled}
      placeholder={placeholder ?? "Search & Select Patient"}
      enableFilterOption
      renderCustomLabel={(option) => (
        <ListItemText
          primary={
            <Flex gap={1}>
              <Text>{option.label}</Text>
              <Text>{NonNullableDate(option.birthDate as any)}</Text>
            </Flex>
          }
          secondary={
            showEmail ? (
              <Text variant="subtitle2">
                {option?.email ? `(${option.email})` : ""}
              </Text>
            ) : undefined
          }
        />
      )}
      options={options}
      value={value}
      onSearch={handleSearch}
      onChange={onChange}
      name={name}
      handleBlur={handleBlur}
      pagination={pagination}
      setPagination={setPagination}
      totalElements={patientList?.data?.page?.totalElements}
    />
  );
};

export default PatientSelectScroll;
